import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styles from './NPSForm.module.css';
import RemotePath from '../../consts/RemotePaths';
import { DOMAIN_URL } from '../../consts/DomainConfig';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import CustomButton from '../../components/buttons/CustomButton';
import PublicPrivacyFooter from '../../components/publicPageFooter/PublicPrivacyFooter';

function NPSForm() {
  const { token: routeToken } = useParams();
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const token = routeToken || searchParams.get('i');
  
  const [selectedScore, setSelectedScore] = useState(null);
  const [comments, setComments] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [appointmentInfo, setAppointmentInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPromoter, setIsPromoter] = useState(false);
  const [pathLogo, setPathLogo] = useState('');
  const [scoreConfirmed, setScoreConfirmed] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Função para detectar ambiente WhatsApp
  const isWhatsAppEnvironment = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /WhatsApp/i.test(userAgent) || 
           /wv.*WhatsApp/i.test(userAgent) ||
           window.location.href.includes('whatsapp://') ||
           document.referrer.includes('whatsapp');
  };

  // Função robusta para abrir links externos
  const openExternalLink = async (url) => {
    if (!url) return false;

    const isWhatsApp = isWhatsAppEnvironment();
    
    try {
      if (isWhatsApp) {
        // No WhatsApp, usar window.location.href diretamente
        window.location.href = url;
        return true;
      } else {
        // Em navegadores normais, sempre tentar window.open
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        
        if (newWindow && !newWindow.closed) {
          return true;
        } else {
          // Se window.open falhou, mostrar mensagem para o usuário
          
          
          // Criar um link temporário e simular clique
          const tempLink = document.createElement('a');
          tempLink.href = url;
          tempLink.target = '_blank';
          tempLink.rel = 'noopener noreferrer';
          document.body.appendChild(tempLink);
          tempLink.click();
          document.body.removeChild(tempLink);
          
          return true;
        }
      }
    } catch (error) {
      
      
      // Último recurso: criar link temporário
      try {
        const tempLink = document.createElement('a');
        tempLink.href = url;
        tempLink.target = '_blank';
        tempLink.rel = 'noopener noreferrer';
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
        return true;
      } catch (finalError) {
        
        return false;
      }
    }
  };

  useEffect(() => {
    const fetchAppointmentInfo = async () => {
      try {
        if (!token) {
          setError('Link inválido. Por favor, verifique a URL.');
          setIsLoading(false);
          return;
        }

        const response = await fetch(
          `${DOMAIN_URL}api/get_nps_appointment_info.php?i=${encodeURIComponent(token)}`,
          {
            headers: {
              'Accept': 'application/json'
            }
          }
        );

        if (!response.ok) {
          if (response.status == 429) {
            throw new Error('Muitas requisições. Por favor, tente novamente mais tarde.');
          }
          throw new Error('Erro ao carregar informações');
        }

        const data = await response.json();

        if (data.status == 'success') {
          setAppointmentInfo(data.data);
          if (data.data.company_logo) {
            setPathLogo(RemotePath.PATH_USER_UPLOADS_COMPANY_LOGO(data.data.companyId) + data.data.company_logo);
          }
        } else {
          throw new Error(data.message || 'Erro ao carregar informações');
        }
      } catch (err) {
        setError(err.message || 'Não foi possível carregar as informações do agendamento.');
        
      } finally {
        setIsLoading(false);
      }
    };

    fetchAppointmentInfo();
  }, [token]);

  const getScoreCategory = (score) => {
    if (score >= 9) return 'promoter';
    if (score >= 7) return 'neutral';
    return 'detractor';
  };

  const saveToAPI = async (score, comment = '') => {
    const formData = new FormData();
    formData.append('i', token);
    formData.append('score', score);
    formData.append('comments', comment.trim());

    try {
      const response = await fetch(`${DOMAIN_URL}api/set_nps_response.php`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        if (response.status == 429) {
          throw new Error('Muitas requisições. Por favor, tente novamente mais tarde.');
        }
        throw new Error('Erro ao enviar avaliação');
      }

      const data = await response.json();
      if (data?.status != "Success") {
        throw new Error(data?.message || 'Falha ao enviar avaliação');
      }
    } catch (err) {
      
    }
  };

  const handleScoreSelect = (score) => {
    setSelectedScore(score);
    setIsPromoter(score >= 9);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedScore == null) {
      setError('Por favor, selecione uma nota antes de enviar.');
      return;
    }

    if (!token) {
      setError('Link inválido. Por favor, verifique a URL.');
      return;
    }

    if (!scoreConfirmed) {
      // Salva a resposta quando confirma a nota
      await saveToAPI(selectedScore, comments);
      setScoreConfirmed(true);
      return;
    }

    // Se for promoter, não faz nada após confirmar a nota
    if (isPromoter) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append('i', token);
    formData.append('score', selectedScore);
    formData.append('comments', comments.trim());

    try {
      const response = await fetch(`${DOMAIN_URL}api/set_nps_response.php`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        if (response.status == 429) {
          throw new Error('Muitas requisições. Por favor, tente novamente mais tarde.');
        }
        throw new Error('Erro ao enviar avaliação');
      }

      const data = await response.json();

      if (data?.status == "Success") {
        setIsSubmitted(true);
      } else {
        throw new Error(data?.message || 'Falha ao enviar avaliação');
      }
    } catch (err) {
      setError(err.message || 'Ocorreu um erro ao enviar sua avaliação. Por favor, tente novamente.');
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleReview = async () => {
    if (!token || !appointmentInfo?.google_review_url) {
      
      return;
    }

    setIsRedirecting(true);
    setError(null);

    try {
      // Primeiro envia a avaliação
      const formData = new FormData();
      formData.append('i', token);
      formData.append('score', selectedScore);
      formData.append('comments', '');

      const responseNPS = await fetch(`${DOMAIN_URL}api/set_nps_response.php`, {
        method: 'POST',
        body: formData
      });

      if (!responseNPS.ok) {
        throw new Error('Erro ao enviar avaliação');
      }

      const dataNPS = await responseNPS.json();
      if (dataNPS?.status != "Success") {
        throw new Error(dataNPS?.message || 'Falha ao enviar avaliação');
      }

      // Depois atualiza o status do review
      const responseReview = await fetch(`${DOMAIN_URL}api/update_nps_google_review.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          'i': token,
          'status': 'completed'
        })
      });

      if (!responseReview.ok) {
        throw new Error('Erro ao atualizar status do review');
      }

      
      // Usar a nova função robusta para abrir o link
      const success = await openExternalLink(appointmentInfo.google_review_url);
      
      if (!success) {
        setError('Não foi possível abrir o link do Google. Por favor, tente novamente.');
      }
    } catch (err) {
      setError(err.message || 'Ocorreu um erro ao processar sua avaliação.');
      
    } finally {
      setIsRedirecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.formContainer}>
          <div className={styles.loadingMessage}>
            <p>Carregando informações...</p>
          </div>
        </div>
        <PublicPrivacyFooter />
      </div>
    );
  }

  if (error && !appointmentInfo) {
    return (
      <div className={styles.container}>
        <div className={styles.formContainer}>
          <div className={styles.errorMessage}>
            <p>{error}</p>
          </div>
        </div>
        <PublicPrivacyFooter />
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className={styles.container}>
        <div className={styles.formContainer}>
          <div className={styles.headerContainer}>
            {appointmentInfo?.company_logo && (
              <div className={styles.companyLogo}>
                <img src={pathLogo} alt={appointmentInfo.company_name} />
              </div>
            )}
            <AreaTitle
              Title="Avaliação Enviada"
              Description=""
            />
          </div>
          <div className={styles.thankYouMessage}>
            <h2>Muito Obrigada!</h2>
            {isPromoter && appointmentInfo?.google_review_url ? (
              <>
                <p>Sua opinião é muito importante para nós.</p>
                
                {/* Botão redesenhado com melhorias */}
                <div className={styles.buttonContainer}>
                  <CustomButton
                    onClick={handleGoogleReview}
                    style="secondary"
                    variant="contained"
                    label={isRedirecting ? "Redirecionando..." : "Compartilhar experiência"}
                    icon={!isRedirecting && <StarOutlineIcon />}
                    loading={isRedirecting}
                    aria-label="Compartilhar experiência no Google"
                    className={styles.improvedGoogleButton}
                  />
                </div>
              </>
            ) : (
              <p>Agradecemos pela confiança!</p>
            )}
          </div>
        </div>
        <PublicPrivacyFooter />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <div className={styles.headerContainer}>
          {appointmentInfo?.company_logo && (
            <div className={styles.companyLogo}>
              <img src={pathLogo} alt={appointmentInfo.company_name} />
            </div>
          )}
          {(!scoreConfirmed || !isPromoter) && (
            <AreaTitle
              Title="Pesquisa de satisfação"
              Description="Em uma escala de 0 a 10, qual a probabilidade de você recomendar nosso trabalho para amigos e familiares?"
            />
          )}
        </div>

      <form onSubmit={handleSubmit}>
        {!scoreConfirmed && (
          <>
            <div className={styles.scoreSelector}>
              {[...Array(11)].map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`${styles.scoreButton} ${
                    selectedScore == index ? styles.selected : ''
                  } ${selectedScore == index ? styles[getScoreCategory(index)] : ''}`}
                  onClick={() => handleScoreSelect(index)}
                >
                  {index}
                </button>
              ))}
            </div>

            <div className={styles.scoreLabels}>
              <span>Pouco provável</span>
              <span>Muito provável</span>
            </div>
          </>
        )}

        {scoreConfirmed && !isPromoter && (
          <div className={styles.scoreConfirmation}>
            <h3>Nota {selectedScore}</h3>
            <p className={`${styles.scoreStatus} ${styles[getScoreCategory(selectedScore)]}`}>
              {selectedScore >= 7 ? (
                'Agradecemos sua avaliação e gostaríamos de saber como podemos melhorar ainda mais.'
              ) : (
                'Lamentamos não ter atendido suas expectativas.'
              )}
            </p>
            <p className={styles.feedbackTitle}>Sua opinião é muito importante!</p>
            <p className={styles.feedbackRequest}>
              Por favor, conte-nos mais sobre sua experiência para que possamos melhorar nossos serviços.
            </p>
          </div>
        )}

        {scoreConfirmed && (
          <>
            {isPromoter ? (
              <div className={styles.thankYouMessage}>
                <AreaTitle
                  Title="Obrigada pela sua avaliação!"
                  Description="Sua opinião no Google ajudará outras pessoas a conhecerem nosso trabalho."
                />
                <p><b>Podemos contar com sua avaliação no Google?</b></p>
                
                {/* Botão redesenhado com melhorias */}
                <div className={styles.buttonContainer}>
                  <CustomButton
                    onClick={handleGoogleReview}
                    style="secondary"
                    variant="contained"
                    label={isRedirecting ? "Redirecionando..." : "Compartilhar experiência"}
                    icon={!isRedirecting && <StarOutlineIcon />}
                    loading={isRedirecting}
                    aria-label="Compartilhar experiência no Google"
                    className={styles.improvedGoogleButton}
                  />
                </div>
              </div>
            ) : (
              <>
                <div className={styles.commentsSection}>
                  <div className={styles.commentsInputContainer}>
                    <textarea
                      id="comments"
                      className={styles.commentsInput}
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      placeholder="Descreva aqui sua experiência..."
                    />
                  </div>
                </div>

                {error && <div className={styles.errorMessage}>{error}</div>}

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Avaliação'}
                </button>
              </>
            )}
          </>
        )}

        {!scoreConfirmed && (
          <>
            {error && <div className={styles.errorMessage}>{error}</div>}
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Enviando...' : 'Confirmar Nota'}
            </button>
          </>
        )}
      </form>
      </div>
      <PublicPrivacyFooter />
    </div>
  );
}

export default NPSForm;